import React, { FC, useCallback, useState } from 'react'
import { Box, Select, MenuItem, Typography } from '@mui/material'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { NewsFormValues, CONTENT_TYPES } from './newsForm.constants'
import NewsFormFields from './NewsFormFields'
import NewsContentFields from './NewsContentFields'
import { useNewsFormHandlers } from './useNewsFormHandlers'

interface NewsFormProps {
  values: NewsFormValues
  editingId: string | null
  isSubmitting: boolean
  setFieldValue: (field: keyof NewsFormValues, value: any) => void
  onSubmit: (e?: React.FormEvent) => Promise<void>
  onReset: () => void
}

const NewsForm: FC<NewsFormProps> = ({
  values,
  editingId,
  isSubmitting,
  setFieldValue,
  onSubmit,
  onReset,
}) => {
  const [dateError, setDateError] = useState(false)

  const handlers = useNewsFormHandlers({ setFieldValue, setDateError })

  const handleFormSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      if (values.date == null) {
        setDateError(true)
      } else {
        setDateError(false)
      }
      await onSubmit(e)
    },
    [values.date, onSubmit]
  )

  return (
    <Box
      sx={{
        mt: 4,
        border: '1px solid #e5e7eb',
        borderRadius: 7,
        p: { xs: 1, md: 5 },
        px: { xs: 2, md: 8 },
        m: 3,
        backgroundColor: '#fff',
      }}
      component="form"
      onSubmit={handleFormSubmit}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 6,
        }}
      >
        <FormatListBulletedIcon />
        <Typography variant="h5" fontWeight={500}>
          {editingId ? 'Editar Noticia' : 'Agregar Noticias y Comunicados'}
        </Typography>
      </Box>

      {/* Content Type */}
      <Box sx={{ mb: 4, width: '30%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Tipo de contenido
        </Typography>
        <Select
          value={values.contentType}
          onChange={handlers.handleContentTypeChange}
          fullWidth
          sx={{
            maxWidth: 300,
            height: '40px',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          {CONTENT_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Form Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 0, md: 4 },
          alignItems: 'start',
        }}
      >
        {/* Left Column */}
        <NewsFormFields
          title={values.title}
          tag={values.tag}
          site={values.site}
          date={values.date}
          dateError={dateError}
          contentType={values.contentType}
          image={values.image}
          document={values.document}
          onTitleChange={handlers.handleTitleChange}
          onTagChange={handlers.handleTagChange}
          onSiteChange={handlers.handleSiteChange}
          onDateChange={handlers.handleDateChange}
          onImageSelect={handlers.handleFileSelect}
          onDocumentSelect={handlers.handleDocumentSelect}
          onRemoveImage={handlers.handleRemoveImage}
          onRemoveDocument={handlers.handleRemoveDocument}
        />

        {/* Right Column */}
        <NewsContentFields
          description={values.description}
          body={values.body}
          editingId={editingId}
          isSubmitting={isSubmitting}
          onDescriptionChange={handlers.handleDescriptionChange}
          onBodyChange={handlers.handleBodyChange}
          onReset={onReset}
        />
      </Box>
    </Box>
  )
}

export default NewsForm
